import React from 'react'
import ReactApexChart from 'react-apexcharts'
import dayjs from 'dayjs'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import usePrGraphContainerStyles from './styles'
import { LinearBuffer } from '../../../PlantTechicalView/components/utils/LinearBuffer'

const PrGraphContainer = ({
  openTable,
  mapTotalenergySeries,
  chartData: initialChartData,
  totalEnergygraphDuration,
  isData,
}) => {
  const classes = usePrGraphContainerStyles()

  const isDailyFormat = React.useMemo(() => {
    if (!mapTotalenergySeries || !Array.isArray(mapTotalenergySeries)) {
      return false
    }

    if (['1m', 'mtd'].includes(totalEnergygraphDuration)) {
      return true
    }

    //For custom periods, check if dates are daily (not monthly)
    if (totalEnergygraphDuration === 'custom' && mapTotalenergySeries.length > 0) {
      const firstPeriod = mapTotalenergySeries[0]?.period
      if (firstPeriod) {
        const hasDay = firstPeriod.split('-').length === 3
        return hasDay
      }
    }

    return false
  }, [mapTotalenergySeries, totalEnergygraphDuration])

  const processedData = React.useMemo(() => {
    if (!mapTotalenergySeries || !Array.isArray(mapTotalenergySeries)) {
      return []
    }

    return mapTotalenergySeries.map((item) => {
      // Make sure PR is within the expected range (0-100%)
      const normalizedPR = Math.min(Math.max(Number(item.averagePR), 0), 100)

      return {
        x: isDailyFormat
          ? dayjs(item.period).format('DD-MM')
          : dayjs(item.period).format('MMM-YYYY'),
        y: normalizedPR,
        totalYield: item.totalYield || 0,
        totalGti: item.totalGti || 0,
        originalPeriod: item.period, // for table
      }
    })
  }, [mapTotalenergySeries, isDailyFormat])

  // Calculate the maximum value for the y-axis based on data
  const maxValue = React.useMemo(() => {
    if (!processedData.length) return 100
    
    // Find the maximum PR value in the data
    const dataMax = Math.max(...processedData.map(item => item.y))
    
    // If values are very small (less than 5%), set max to 5% to make bars visible
    if (dataMax < 5) return 5
    
    // For larger values, add 10% headroom but cap at 100%
    return Math.min(Math.ceil(dataMax * 1.1), 100)
  }, [processedData])

  // Calculate tick values for y-axis based on the maximum value
  const yAxisTicks = React.useMemo(() => {
    const ticks = []
    const tickCount = 5 // Number of ticks we want
    
    for (let i = 0; i <= tickCount; i++) {
      ticks.push((maxValue / tickCount) * i)
    }
    
    return ticks
  }, [maxValue])

  const updatedChartOptions = {
    chart: {
      type: 'bar',
      height: 400,
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
        },
      },
    },
    series: [
      {
        name: 'Aggregated PR',
        data: processedData,
      },
    ],
    yaxis: {
      min: 0,
      max: maxValue, // Dynamic maximum based on data
      tickAmount: 5,
      labels: {
        formatter: (value) => `${value.toFixed(2)}%`,
        style: { fontSize: '12px' },
      },
      title: {
        text: `Aggregated PR (%) `,
        style: { color: '#5968B7', fontSize: '13px' },
      },
    },
    tooltip: {
      x: { formatter: (value) => value },
      y: {
        formatter: (value, { seriesIndex, dataPointIndex, w }) => {
          const data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
          // Show more decimal places for small values
          return `Average PR: ${data.y < 1 ? data.y.toFixed(3) : data.y.toFixed(2)}%`
        },
      },
      shared: true,
      intersect: false,
    },
    xaxis: {
      type: 'category',
      labels: {
        rotate: -40,
        rotateAlways: true,
        style: { fontSize: '12px' },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '60%',
        borderRadius: 2,
        dataLabels: {
          position: 'top', // Places the label at the top of the bar
        },
      },
    },
    colors: ['#5968B7'],
    fill: {
      colors: ['#5968B7'],
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: 'vertical',
        shadeIntensity: 0.25,
        inverseColors: true,
        opacityFrom: 0.85,
        opacityTo: 0.85,
      },
    },
    stroke: { width: 1, colors: ['#5968B7'] },
    dataLabels: { 
      enabled: processedData.length < 15, // Only show data labels when there are few bars
      formatter: function(val) {
        // Show more decimal places for small values
        return val < 1 ? val.toFixed(3) + '%' : val.toFixed(2) + '%'
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    grid: {
      borderColor: '#e7e7e7',
      row: {
        colors: ['#f3f3f3', 'transparent'],
        opacity: 0.5
      },
    },
  }

  const getDateFormat = () =>
    isDailyFormat ? 'YYYY-MM-DD' : 'YYYY-MM'

  return (
    <div className={classes.root}>
      {openTable ? (
        <div style={{ maxHeight: '360px', overflow: 'auto' }}>
          <TableContainer component={Paper}>
            <Table aria-label="Performance Ratio Data Table">
              <TableHead>
                <TableRow>
                  <TableCell>
                    {isDailyFormat ? 'Date' : 'Month'}
                  </TableCell>
                  <TableCell align="right">
                    Aggregated PR (Average) (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {processedData.map((row, index) => (
                  <TableRow
                    key={row.x}
                    className={index % 2 === 0 ? 'whiteRow' : 'grayRow'}
                  >
                    <TableCell>
                      {dayjs(row.originalPeriod).format(getDateFormat())}
                    </TableCell>
                    <TableCell align="right">
                      {/* Show more decimal places for small values */}
                      {row.y < 1 ? row.y.toFixed(3) : row.y.toFixed(2)}%
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      ) : (
        <div>
          {!isData ? (
            <div
              style={{
                padding: '100px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
                marginTop: '100px',
              }}
            >
              <LinearBuffer />
            </div>
          ) : processedData.length === 0 ? (
            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '300px',
                fontSize: '16px',
                color: '#666',
              }}
            >
              No data available for the selected time period
            </div>
          ) : (
            <div id="chart">
              <ReactApexChart
                options={updatedChartOptions}
                series={updatedChartOptions.series}
                type="bar"
                height={400}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default PrGraphContainer
